import SeoHead from '@presentation/seo/SeoHead'

type LegalSection = {
  title: string
  paragraphs: string[]
}

const TERMS_OF_SERVICE_SECTIONS: LegalSection[] = [
  {
    title: 'Acceptance of Terms',
    paragraphs: [
      'By accessing and using the Parkinson\'s Insights website and associated services, including the PD Buddy app and No Silver Bullet 4PD content, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you must not use our services.',
    ],
  },
  {
    title: 'Nature of the Service',
    paragraphs: [
      'Parkinson\'s Insights is an educational and community platform designed to share holistic strategies for living with Parkinson\'s as a condition. Our services include the provision of articles, webinars, community forums, and a symptom-tracking application. You acknowledge that our platform provides information and community support, not clinical medical services.',
    ],
  },
  {
    title: 'User Eligibility and Accounts',
    paragraphs: [
      'Our services are intended for individuals who are at least 18 years old. When you create an account on the PD Buddy app or register for a No Silver Bullet 4PD webinar, you agree to:',
      'Provide accurate and complete information.',
      'Maintain the security and confidentiality of your login credentials.',
      'Notify us immediately at support@goodvibestechnologies.com if you suspect any unauthorised use of your account.',
    ],
  },
  {
    title: 'Intellectual Property Rights',
    paragraphs: [
      'All content on the Parkinson\'s Insights platform - including but not limited to text, graphics, logos, webinar recordings, and the underlying code of the PD Buddy app - is the property of Good Vibes Technologies Ltd and No Silver Bullet 4PD.',
      'You are granted a limited, non-exclusive license to access the content for personal, non-commercial use.',
      'You may not reproduce, distribute, or create derivative works from our content without express written permission from the founders.',
    ],
  },
  {
    title: 'User-Generated Content and Conduct',
    paragraphs: [
      'In our community forums or during live webinars, you may have the opportunity to share your own experiences. By posting content, you grant us a worldwide, royalty-free license to use that content for educational purposes. You agree not to:',
      'Post content that is defamatory, illegal, or promotes medical misinformation.',
      'Harass or abuse other members of the community.',
      'Use our platform to advertise or sell third-party products without our prior consent.',
    ],
  },
  {
    title: 'Limitation of Liability',
    paragraphs: [
      'To the fullest extent permitted by law, Parkinson\'s Insights, Good Vibes Technologies Ltd, and No Silver Bullet 4PD shall not be liable for any direct, indirect, or consequential damages arising from your use of our services. This includes, but is not limited to, any health outcomes or reliance on information shared by guest experts in our webinars. Our total liability for any claim arising out of these terms shall not exceed the amount you paid us (if any) to use the services.',
    ],
  },
  {
    title: 'Modifications to the Service',
    paragraphs: [
      'We reserve the right to modify or discontinue any part of our services, including the PD Buddy app features or the frequency of No Silver Bullet 4PD webinars, at any time without prior notice. We also reserve the right to update these Terms of Service periodically.',
    ],
  },
  {
    title: 'Governing Law',
    paragraphs: [
      'These terms are governed by and construed in accordance with the laws of England and Wales. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of England.',
    ],
  },
  {
    title: 'Contact Information',
    paragraphs: [
      'For any questions regarding these Terms of Service, please contact us at: support@goodvibestechnologies.com.',
    ],
  },
]

export default function TermsOfServicePage() {
  return (
    <main className="bg-background py-8 sm:py-12">
      <SeoHead
        title="Terms of Service | Parkinson's Insights"
        description="Read the terms governing use of Parkinson's Insights, PD Buddy, and related educational services."
        canonicalPath="/legal/terms-of-service"
      />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-border-light bg-surface p-6 shadow-sm sm:p-8">
          <header className="border-b border-border-light pb-5 sm:pb-6">
            <h1 className="text-2xl font-bold tracking-tight text-primary sm:text-3xl">
              Terms of Service
            </h1>
            <p className="mt-2 text-sm font-medium text-text-secondary">
              Last Updated: 13 March 2026
            </p>
          </header>

          {TERMS_OF_SERVICE_SECTIONS.map((section) => (
            <section
              key={section.title}
              className="border-b border-border-light py-5 last:border-b-0 last:pb-0 sm:py-6"
            >
              <h2 className="text-xl font-bold tracking-tight text-primary sm:text-2xl">
                {section.title}
              </h2>
              <div className="mt-4 space-y-4">
                {section.paragraphs.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="text-base leading-relaxed text-text-primary"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </main>
  )
}
